import api from '@/lib/axios';
import type {
  WorkspaceEmailProviderMap,
  WorkspaceEmailResult,
  WorkspacePhoneProviderMap,
  WorkspacePhoneProviderRecord,
  WorkspacePhoneResult,
  WorkspaceLookalikeItem,
  WorkspaceRow,
  WorkspaceUploadType,
} from '@/lib/types/workspace.types';

/** Pulls the proxy's forwarded error message out of an RTK Query mutation rejection. */
export function getWorkspaceErrorMessage(err: unknown): string {
  const message = (err as { data?: { message?: string } })?.data?.message;
  if (message) return message;
  // No JSON error body came back at all (server unreachable, rewrite
  // gateway HTML, timeout) — say that instead of a blind "something went
  // wrong" so the next failure points at the transport, not the feature.
  const status = (err as { status?: unknown })?.status;
  return status !== undefined && status !== null
    ? `Request failed (${String(status)}) — please try again`
    : 'Something went wrong — please try again';
}

/**
 * The one field the Workspace API actually matches rows on — everything else
 * in the uploaded file comes along for free (the backend copies the whole raw
 * row into `input`), so this is the only mapping the import flow asks for.
 */
/**
 * "3 mins ago" for a timestamp. Lifted out of WorkspaceJobList when the
 * overview needed the same thing — it was already duplicated there and in
 * EnrichmentJobList.
 */
export function relativeDate(dateStr: string): string {
  try {
    const diffMins = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins !== 1 ? 's' : ''} ago`;
    const diffHrs = Math.floor(diffMins / 60);
    if (diffHrs < 24) return `${diffHrs} hour${diffHrs !== 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHrs / 24);
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
  } catch {
    return dateStr;
  }
}

export const LINKEDIN_TARGET_FIELD: Record<WorkspaceUploadType, string> = {
  contact: 'contLinkedin',
  company: 'compLinkedin',
};

const LINKEDIN_HEADER_HINTS = ['linkedin', 'linkedinurl', 'linkedinprofile', 'linkedinprofileurl', 'contactlinkedin', 'companylinkedin'];

function normalizeHeader(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

export function looksLikeLinkedinUrl(value: string): boolean {
  return /linkedin\.com\//i.test(value);
}

/**
 * Turns a `link` cell's value into a safe href, or null when it isn't one.
 *
 * Format only — whether the page actually exists can't be checked from the
 * browser (CORS), so a well-formed dead link still passes. Bare domains
 * ("acme.com", "linkedin.com/in/x") get https:// prepended; any other scheme
 * (javascript:, data:, mailto:) is rejected so an imported CSV can't plant a
 * script link in the grid.
 */
export function toSafeHref(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed || /\s/.test(trimmed)) return null;
  // No "." in the scheme pattern, so "acme.com:8080" reads as host:port.
  const hasScheme = /^[a-z][a-z0-9+-]*:/i.test(trimmed);
  if (hasScheme && !/^https?:\/\//i.test(trimmed)) return null;
  let url: URL;
  try {
    url = new URL(hasScheme ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
  // Needs a dot and a 2+ letter TLD — rules out "N/A", "abc", "localhost".
  if (!/\.[a-z]{2,}$/i.test(url.hostname)) return null;
  return url.href;
}

/**
 * Finds which uploaded column holds the LinkedIn URL. Tries the header name
 * first (handles "LinkedIn Profile"/"Company LinkedIn" instantly), then falls
 * back to scanning the sample row's actual values for a linkedin.com URL —
 * which catches oddly-named headers (a generic "URL"/"Profile" column, a
 * translated header) where the data gives it away but the name doesn't.
 */
export function guessLinkedinHeader(headers: string[], firstRow: string[]): string {
  const byHeader = headers.find((h) => LINKEDIN_HEADER_HINTS.some((hint) => normalizeHeader(h).includes(hint)));
  if (byHeader) return byHeader;

  const byValueIndex = firstRow.findIndex((value) => looksLikeLinkedinUrl(value ?? ''));
  return byValueIndex >= 0 ? headers[byValueIndex] ?? '' : '';
}

/**
 * Every field a workspace_data row carries that isn't uploaded/enriched data.
 *
 * Verified against the service that writes them — Infynd-CL-Enrichment-Service
 * `app/internal/workspace.py`, which builds each doc as userupload_id/user_id/
 * type/input/order_no/createdate, then the mappingtype targets, then
 * person_id/company_id/enriched/enriched_data. So the only top-level key this
 * list deliberately lets through is a mappingtype target (contLinkedin /
 * compLinkedin) — every other name appearing here would be a new service
 * field, not user data.
 */
const ROW_META_KEYS = new Set([
  '_id',
  'userupload_id',
  'user_id',
  'type',
  'order_no',
  'input',
  'person_id',
  'company_id',
  'enriched',
  'enriched_data',
  'createdate',
  'updatedate',
  // Written by mergeEnrichedFieldsIntoInput itself, not by the service. Listed
  // so a second pass over an already-merged row can't fold this object into
  // `input` as if it were a column of its own.
  'email_columns',
  'phone_columns',
  'lookalike_columns',
]);

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Canonical form of a column's display name, for "does this column already
 * exist?" comparisons.
 *
 * Shared rather than redefined per call site because every place that names a
 * column has to agree on the answer: the add-column sheet, both enrichment
 * mapping panes and the edit-column modal all guard against the same
 * collision, and a guard that normalised differently from the next one would
 * let a duplicate straight through the gap between them.
 */
export function normalizeColumnName(name: string): string {
  return name.trim().toLowerCase();
}

/**
 * Which of `names` would collide — with an existing grid column, or with
 * another entry in `names` itself.
 *
 * Both halves matter for the enrichment mapping panes: their targets become
 * real columns (the service writes `enriched_data.{target}`, which
 * mergeEnrichedFieldsIntoInput folds into `input`), so a target matching an
 * uploaded column silently overwrites that column's values, and two targets
 * matching *each other* collapse into one key in the request body — quietly
 * dropping one of the fields the user asked for.
 */
export function findDuplicateColumnNames(names: string[], existingNames: string[]): Set<string> {
  const existing = new Set(existingNames.map(normalizeColumnName));
  const counts = new Map<string, number>();
  for (const name of names) {
    const normalized = normalizeColumnName(name);
    if (!normalized) continue;
    counts.set(normalized, (counts.get(normalized) ?? 0) + 1);
  }

  const duplicates = new Set<string>();
  for (const [normalized, count] of counts) {
    if (existing.has(normalized) || count > 1) duplicates.add(normalized);
  }
  return duplicates;
}

/**
 * Whether an `enriched_data` value is an email column's provider map rather
 * than a plain scalar.
 *
 * Needed because the grid can't tell from the column *name*: a column added in
 * a previous session is bootstrapped from the row's keys with no memory of
 * which enrichment produced it, and every other enrichment type writes a
 * string here. Rendering one of these maps as a value is what put
 * "[object Object]" in the cell.
 *
 * Deliberately strict — every entry must be an object carrying at least one
 * field of the record shape — so a legitimately object-valued column from some
 * future enrichment isn't swallowed by this branch.
 */
export function isEmailProviderMap(value: unknown): value is WorkspaceEmailProviderMap {
  if (!isPlainObject(value)) return false;
  const entries = Object.values(value);
  if (entries.length === 0) return false;
  return entries.every(
    (entry) =>
      isPlainObject(entry) &&
      ('email' in entry || 'email_verification' in entry || 'status' in entry || 'verification' in entry)
  );
}

/**
 * Picks the one address an email column should show for a row.
 *
 * Ranked rather than "first entry wins", because entry order says nothing
 * about answer quality: a `verified` record wins outright, then a settled
 * `found` one, and only then whatever else carries an address.
 *
 * That last tier matters more than it looks. Real data (2026-09-07) has
 * entries shaped `{ email: "…", email_verification: "inprocess",
 * verification: true, status: null }` — an address the provider really
 * returned, with **no `status` field at all** while its verification is
 * outstanding. Requiring `status === "found"` discarded those, so a row that
 * had shown an address during the run came back as "No email found" after a
 * reload. Anything with an email is a result; `status` only ranks it.
 */
/**
 * Whether an `enriched_data` value is a phone column's provider map rather
 * than a plain scalar.
 *
 * Checked (and must stay checked) BEFORE `isEmailProviderMap` in the loop
 * below: a phone entry's fields (`directdials`, `phone_number`, `status`)
 * satisfy that check's looser `'status' in entry` branch too, since both
 * enrichments share a generic `status` field. Without this dedicated check
 * (and without it running first), every phone column got folded into
 * `email_columns` instead of `phone_columns` — `row.phone_columns` was never
 * populated at all, and the column silently rendered through
 * WorkspaceEmailCell ("No email found" on every row) instead of
 * WorkspacePhoneCell, even though the raw rows response carried a real
 * number.
 *
 * Keyed on presence (`in`), not truthiness — a miss still carries these keys
 * with `null` values (e.g. `{ directdials: null, phone_number: null, status:
 * "not_found" }`), and that shape is exactly what tells a phone entry apart
 * from an email one that also merely lacks a value.
 */
export function isPhoneProviderMap(value: unknown): value is WorkspacePhoneProviderMap {
  if (!isPlainObject(value)) return false;
  const entries = Object.values(value);
  if (entries.length === 0) return false;
  return entries.every(
    (entry) =>
      isPlainObject(entry) &&
      ('directdials' in entry || 'phone_number' in entry || 'phone' in entry || 'phoneNumber' in entry || 'mobile' in entry)
  );
}

/**
 * Picks the one phone number a provider entry carries, whichever of the
 * observed shapes it used — same normalisation `PhoneUnlockCell.tsx`'s
 * `firstPhone` already does for `contact.phoneProviders`, since both come
 * from the same Provider-Service records. Exported so the cell's own
 * providers-modal mapping (WorkspacePhoneCell) can reuse it instead of a
 * third copy of this normalisation.
 */
export function firstPhone(entry: WorkspacePhoneProviderRecord): string | undefined {
  return (
    entry.phone ||
    entry.phoneNumber ||
    entry.directdials?.[0] ||
    entry.phone_number?.mobile?.[0] ||
    (Array.isArray(entry.mobile) ? entry.mobile[0] : undefined) ||
    undefined
  );
}

/** Whether a provider entry counts as a hit — `status`/`phoneFlag`, mirroring `PhoneUnlockCell.tsx`'s `isFound`. */
export function isPhoneFound(entry: WorkspacePhoneProviderRecord): boolean {
  const flag = entry.status ?? entry.phoneFlag;
  return flag === 'found' || flag === 'success';
}

/**
 * Picks the one number a phone column should show for a row — same ranking
 * rule as `resolveEmailProviderMap`: a flagged find wins over "carries a
 * number but wasn't flagged" over nothing, rather than first-entry-wins.
 */
export function resolvePhoneProviderMap(map: WorkspacePhoneProviderMap): WorkspacePhoneResult {
  const entries = Object.values(map);
  const withPhone = entries.filter((entry) => !!firstPhone(entry));
  const best = withPhone.find(isPhoneFound) ?? withPhone[0];

  return {
    phone: best ? (firstPhone(best) ?? null) : null,
    notFound: entries.length > 0 && withPhone.length === 0,
    providers: map,
  };
}

/**
 * Whether an `enriched_data` value is a lookalike column's array of hits
 * (company or contact) rather than a plain scalar.
 *
 * Loose on purpose — no other enrichment here ever writes a plain array to
 * `enriched_data` (email/phone write a provider map keyed by provider name,
 * the mapping types write scalars), so "it's an array of plain objects" is
 * enough to recognise it without a marker field. An empty array counts too:
 * per the lookalike endpoints' own docs, a row with no matched company/
 * contact (or no matchable fields) still gets `enriched_data.{column} = []`
 * — the column exists, it just has nothing for that row.
 */
export function isLookalikeArray(value: unknown): value is WorkspaceLookalikeItem[] {
  return Array.isArray(value) && value.every(isPlainObject);
}

/**
 * Shared shape helpers for one lookalike hit — used by both the grid cell
 * (WorkspaceLookalikeCell) and its modal (WorkspaceLookalikeModal), so the
 * "is this a contact or a company" rule and the title/link derivation live in
 * exactly one place.
 *
 * A contact-shaped hit (POST /workspace/lookalikecontacts) is the only one
 * that ever carries first_name/last_name/designation; a company-shaped one
 * (POST /workspace/lookalikecompanies) never does.
 */
export function isLookalikeContactItem(item: WorkspaceLookalikeItem): boolean {
  return item.first_name != null || item.last_name != null || item.designation != null;
}

export function lookalikeItemTitle(item: WorkspaceLookalikeItem): string {
  if (isLookalikeContactItem(item)) {
    return [item.first_name, item.last_name].filter(Boolean).join(' ') || 'Unnamed contact';
  }
  return item.name || 'Unnamed company';
}

export function lookalikeItemLink(item: WorkspaceLookalikeItem): string | null {
  return item.linkedin_profile || item.linkedin_link || null;
}

/**
 * Avatar-fallback initials for one lookalike hit, from its own real
 * first/last (or company) name — unlike the company-search grid's People
 * column, which hashes a placeholder because that page has no per-contact
 * data at all, a lookalike hit always carries a real name from the match, so
 * this reads it directly rather than fabricating one.
 */
export function lookalikeItemInitials(item: WorkspaceLookalikeItem): string {
  if (isLookalikeContactItem(item)) {
    const combined = `${item.first_name?.[0] ?? ''}${item.last_name?.[0] ?? ''}`;
    return combined ? combined.toUpperCase() : '?';
  }
  const parts = item.name?.trim().split(/\s+/).filter(Boolean) ?? [];
  if (parts.length === 0) return '?';
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** The row's own name, for titling a lookalike modal ("Similar companies for X"). Also usable for email/phone cells' contactName. */
export function deriveWorkspaceRowLabel(input: Record<string, unknown>): string | undefined {
  const firstName = typeof input['First Name'] === 'string' ? input['First Name'] : '';
  const lastName = typeof input['Last Name'] === 'string' ? input['Last Name'] : '';
  const named = `${firstName} ${lastName}`.trim();
  if (named) return named;
  for (const key of ['Name', 'name', 'Company Name', 'company_name', 'Company', 'company']) {
    const value = input[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return undefined;
}

export function resolveEmailProviderMap(map: WorkspaceEmailProviderMap): WorkspaceEmailResult {
  const entries = Object.values(map);
  const withEmail = entries.filter((entry) => !!entry.email);
  const best =
    withEmail.find((entry) => entry.email_verification === 'verified') ??
    withEmail.find((entry) => entry.status === 'found') ??
    withEmail[0];

  return {
    email: best?.email ?? null,
    email_verification: best?.email_verification ?? null,
    // Providers answered and not one of them returned an address. Only
    // meaningful when there were entries at all — an empty map means nothing
    // has run yet.
    notFound: entries.length > 0 && withEmail.length === 0,
    providers: map,
  };
}

/**
 * A workspace_data row spreads its fields across three places, and the grid
 * reads only one of them — so they get folded together here, leaving every
 * column (uploaded or enriched) reachable as `row.input[key]`:
 *
 * 1. `input` — the raw uploaded row, exactly as the CSV had it.
 * 2. top-level siblings — the mappingtype targets, written at process time as
 *    `inserted_doc[value] = row.get(key)` (e.g. `contLinkedin`).
 * 3. `enriched_data` — where fetchDomain / contactdetail / companydetail put
 *    everything they fetch (`$set: {"enriched_data.<column>": ...}`), keyed by
 *    the column name the user chose in "Add column".
 *
 * (3) was previously skipped, so enrichment results never reached the grid: a
 * user added a Find Domain / Contact Details / Company Details column, the job
 * ran and wrote the values, and the column stayed empty forever. They land
 * last here, and so win a name collision with an uploaded column — a freshly
 * fetched value is the one worth showing.
 *
 * An email column is the one enriched value that ISN'T a scalar: it's a
 * per-provider map (see isEmailProviderMap). Those are flattened to the one
 * resolved address before going into `input`, with the full record kept
 * alongside on `email_columns`. Doing it here rather than in the cell means
 * search, sort, filter and CSV export all see a real email string — they read
 * `input` directly and would otherwise every one of them be comparing
 * "[object Object]".
 */
export function mergeEnrichedFieldsIntoInput(row: WorkspaceRow): WorkspaceRow {
  const extra = Object.keys(row).filter((key) => !ROW_META_KEYS.has(key));
  const enriched = row.enriched_data ?? {};
  if (extra.length === 0 && Object.keys(enriched).length === 0) return row;

  const merged = { ...row.input };
  const emailColumns: Record<string, WorkspaceEmailResult> = {};
  const phoneColumns: Record<string, WorkspacePhoneResult> = {};
  const lookalikeColumns: Record<string, WorkspaceLookalikeItem[]> = {};
  const put = (key: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return;
    // An empty object is as empty as '' but renders as "[object Object]" once
    // a cell stringifies it — the same way a provider map used to before the
    // branch below flattened it.
    if (isPlainObject(value) && Object.keys(value).length === 0) return;
    merged[key] = value;
  };
  for (const key of extra) put(key, row[key]);
  // Deliberately NOT filtered by ROW_META_KEYS, unlike the top-level keys
  // above: every key here is a column name the *user typed* in the enrich
  // mapping (the service writes `enriched_data.{user_column}` — see
  // contact-detail-mapping's `{ userColumnName: elasticFieldName }`), so
  // dropping one because it collides with a row field name would silently
  // discard the values of a column they asked for.
  for (const [key, value] of Object.entries(enriched)) {
    if (isLookalikeArray(value)) {
      lookalikeColumns[key] = value;
      // No single scalar to flatten an array of hits to — but the key still
      // has to reach `input` (empty string, same fallback the email branch
      // below uses) or bootstrapColumnsFromKeys never sees the column and it
      // vanishes from the grid on the next load.
      if (!(key in merged)) merged[key] = '';
      continue;
    }
    // Checked before isEmailProviderMap — see isPhoneProviderMap's docstring
    // for why the order matters (a phone entry's fields also satisfy the
    // email check's looser `'status' in entry` branch).
    if (isPhoneProviderMap(value)) {
      const resolved = resolvePhoneProviderMap(value);
      phoneColumns[key] = resolved;
      if (resolved.phone) {
        merged[key] = resolved.phone;
      } else if (!(key in merged)) {
        merged[key] = '';
      }
      continue;
    }
    if (isEmailProviderMap(value)) {
      const resolved = resolveEmailProviderMap(value);
      emailColumns[key] = resolved;
      if (resolved.email) {
        merged[key] = resolved.email;
      } else if (!(key in merged)) {
        // No address, but the providers DID report — so the column exists and
        // must stay. `put` would drop a null and the key would never reach
        // `input`, which is the only thing bootstrapColumnsFromKeys derives
        // the grid's columns from: a page where every row missed lost the
        // column entirely on the next visit. Empty string, not null, so the
        // key is present while the cell still reads as blank (the real verdict
        // comes from `email_columns[key]`).
        //
        // Guarded on absence so this can never blank out a same-named
        // uploaded column that already has a value.
        merged[key] = '';
      }
      continue;
    }
    put(key, value);
  }

  const result: WorkspaceRow = { ...row, input: merged };
  if (Object.keys(emailColumns).length > 0) result.email_columns = emailColumns;
  if (Object.keys(phoneColumns).length > 0) result.phone_columns = phoneColumns;
  if (Object.keys(lookalikeColumns).length > 0) result.lookalike_columns = lookalikeColumns;
  return result;
}

/**
 * Fetches the enriched-results workbook.
 *
 * Deliberately not an RTK Query endpoint: the response is a Blob, and RTK
 * Query puts every result into a fulfilled action's payload and then into the
 * store — which trips the serializability check ("A non-serializable value was
 * detected in an action"). A file is transient, single-use data with nothing
 * to cache, so it goes straight through axios and never touches Redux. Going
 * through the shared instance still gets the auth/refresh interceptor.
 */
export async function fetchWorkspaceWorkbook(userUploadId: string): Promise<Blob> {
  const response = await api.get(`/api/workspace/download/${userUploadId}`, { responseType: 'blob' });
  return response.data as Blob;
}

/** Hands a fetched file to the browser as a download. */
export function saveBlobAs(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Names the downloaded workbook after its sheet. The sheet name is often the
 * original CSV's, so the .csv is dropped rather than producing "leads.csv.xlsx".
 */
export function workbookFilename(sheetName: string): string {
  return `${sheetName.replace(/\.csv$/i, '') || 'workspace'}.xlsx`;
}

function toCsvValue(value: unknown): string {
  const text = value === null || value === undefined ? '' : Array.isArray(value) ? value.join(', ') : String(value);
  return `"${text.replace(/"/g, '""')}"`;
}

/** Exports whatever rows/columns are currently loaded in the browser — the
 * Workspace API has no export endpoint, so this is a client-side stopgap
 * scoped to the loaded page(s), not a full-dataset export. */
export function exportWorkspaceRowsToCsv(rows: WorkspaceRow[], columns: string[], filename: string) {
  const header = columns.map(toCsvValue).join(',');
  const lines = rows.map((row) => columns.map((col) => toCsvValue(row.input?.[col])).join(','));
  const csv = [header, ...lines].join('\n');

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  saveBlobAs(blob, filename.endsWith('.csv') ? filename : `${filename}.csv`);
}
